/**
 * Eric 2026-06-04 — Page "Statut système Resend"
 *
 * Affiche en temps réel :
 *  - Source de la clé (BYOK org / partagée Knoria / aucune)
 *  - Adresse expéditrice configurée
 *  - Domaines vérifiés sur Resend
 *  - Invitations envoyées dans les 24 dernières heures
 *  - Quota approximatif et recommandations
 *
 * Route : /dashboard/admin/email-system-status
 * Permission : admin / founder
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";
import { CheckCircle, XCircle, Warning, ArrowClockwise, Envelope } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

export default function EmailSystemStatusPage() {
  const { lang } = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/org/email/status`, { withCredentials: true });
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">{lang === "fr" ? "Chargement…" : "Loading…"}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-destructive/5 border border-destructive/30 rounded-xl p-5">
          <p className="font-semibold text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  const sourceBadge = {
    byok: { color: "bg-teal-100 text-teal-800 border-teal-300", label: lang === "fr" ? "✓ Clé BYOK de votre org" : "✓ Your org's BYOK key" },
    shared_knoria: { color: "bg-amber-100 text-amber-800 border-amber-300", label: lang === "fr" ? "⚠ Clé partagée Knoria (limitée)" : "⚠ Shared Knoria key (limited)" },
    none: { color: "bg-red-100 text-red-800 border-red-300", label: lang === "fr" ? "✗ Aucune clé configurée" : "✗ No key configured" },
  }[status.source] || { color: "bg-zinc-100 text-zinc-800", label: status.source };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="email-system-status-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Envelope size={26} className="text-primary" />
            {lang === "fr" ? "Statut système Resend" : "Resend System Status"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "fr"
              ? "Vue en temps réel des emails d'invitation, d'approbation et de notification."
              : "Real-time view of invitation, approval, and notification emails."}
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="email-status-refresh-btn">
          <ArrowClockwise size={14} className="mr-1.5" />
          {lang === "fr" ? "Actualiser" : "Refresh"}
        </Button>
      </div>

      {/* Source */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="email-source-section">
        <h2 className="font-semibold text-sm mb-3">{lang === "fr" ? "Source de la clé" : "Key source"}</h2>
        <div className={`inline-block px-3 py-1.5 rounded-lg border text-sm font-semibold ${sourceBadge.color}`} data-testid="email-source-badge">
          {sourceBadge.label}
        </div>
        {status.sender_email && (
          <p className="text-sm mt-3">
            <span className="text-muted-foreground">{lang === "fr" ? "Expéditeur configuré : " : "Configured sender: "}</span>
            <code className="bg-zinc-100 px-2 py-0.5 rounded text-xs">{status.sender_email}</code>
          </p>
        )}
        {status.source === "shared_knoria" && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            ⚠ {lang === "fr"
              ? "Vous utilisez actuellement la clé Resend partagée de Knoria, limitée à 3 000 emails/mois pour TOUS les clients. Pour ne pas dépendre de ce quota partagé, "
              : "You are currently using Knoria's shared Resend key, limited to 3,000 emails/month for ALL customers. To stop depending on this shared quota, "}
            <Link to="/dashboard/settings/ai-package" className="underline font-semibold">
              {lang === "fr" ? "configurez votre propre clé Resend" : "configure your own Resend key"}
            </Link>.
          </div>
        )}
        {status.warning && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-900 text-xs">
            <p className="font-semibold mb-1">{status.warning}</p>
            {status.recommendation && <p>{status.recommendation}</p>}
          </div>
        )}
      </div>

      {/* Domains */}
      {status.domain_status && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="email-domains-section">
          <h2 className="font-semibold text-sm mb-3">{lang === "fr" ? "Domaines Resend" : "Resend domains"}</h2>
          {status.domain_status.error ? (
            <p className="text-sm text-destructive">{status.domain_status.error}</p>
          ) : (
            <>
              {status.domain_status.verified.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-xs font-semibold text-teal-700 uppercase">{lang === "fr" ? "Vérifiés (peuvent envoyer)" : "Verified (can send)"}</p>
                  {status.domain_status.verified.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`email-domain-verified-${i}`}>
                      <CheckCircle size={16} className="text-teal-500 shrink-0" />
                      <code className="text-xs bg-teal-50 px-2 py-0.5 rounded">{d.name}</code>
                      <span className="text-xs text-muted-foreground">{d.region}</span>
                    </div>
                  ))}
                </div>
              )}
              {status.domain_status.pending.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-amber-700 uppercase">{lang === "fr" ? "Non vérifiés (SPF/DKIM manquant)" : "Pending (SPF/DKIM missing)"}</p>
                  {status.domain_status.pending.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm" data-testid={`email-domain-pending-${i}`}>
                      <Warning size={16} className="text-amber-500 shrink-0" />
                      <code className="text-xs bg-amber-50 px-2 py-0.5 rounded">{d.name}</code>
                      <span className="text-xs text-amber-700">{d.status}</span>
                    </div>
                  ))}
                </div>
              )}
              {status.domain_status.verified.length === 0 && status.domain_status.pending.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  {lang === "fr"
                    ? "Aucun domaine ajouté sur Resend. Les emails partiront avec un domaine par défaut (peuvent finir en spam)."
                    : "No domain added on Resend. Emails will use default domain (may land in spam)."}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Recent invites */}
      {status.recent_invites_24h && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="email-recent-section">
          <h2 className="font-semibold text-sm mb-3">
            {lang === "fr" ? `Invitations envoyées (24h) — ${status.recent_invites_count}` : `Invitations sent (24h) — ${status.recent_invites_count}`}
          </h2>
          {status.recent_invites_24h.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {lang === "fr" ? "Aucune invitation envoyée dans les dernières 24 heures." : "No invitations sent in the last 24 hours."}
            </p>
          ) : (
            <div className="space-y-1.5">
              {status.recent_invites_24h.map((inv, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-sm py-1 border-b border-border/30 last:border-0" data-testid={`recent-invite-${i}`}>
                  <span className="text-xs">{inv.email}</span>
                  <span className="text-xs text-muted-foreground">{inv.role}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    inv.status === "accepted" ? "bg-teal-100 text-teal-700" :
                    inv.status === "pending" ? "bg-blue-100 text-blue-700" :
                    "bg-zinc-100 text-zinc-700"
                  }`}>{inv.status}</span>
                  <span className="text-xs text-muted-foreground">{new Date(inv.created_at).toLocaleTimeString(lang === "fr" ? "fr-CA" : "en-CA", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quota info */}
      {status.quota_note && (
        <div className="text-xs text-muted-foreground italic px-2">
          {status.quota_note}
        </div>
      )}
    </div>
  );
}
